import { Button, Skeleton, toast } from '@folio/components';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { EmailAccountDto } from 'korint-sdk';
import {
  emailAccountControllerListMineQueryKey,
  emailAccountControllerListMineQueryOptions,
  emailThreadControllerListThreadsQueryKey,
  generalInboxControllerGetSummaryQueryKey,
  useEmailAccountControllerDisconnect,
} from 'korint-sdk';
import { Plus } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';
import { AccountCard } from './AccountCard';
import { DisconnectAccountDialog } from './DisconnectAccountDialog';
import { DrawerPanel } from './DrawerPanel';

function AccountGroup({
  title,
  hint,
  accounts,
  canManageSharedAccounts,
  onDelete,
}: {
  title: string;
  hint: string;
  accounts: EmailAccountDto[];
  canManageSharedAccounts: boolean;
  onDelete: (account: EmailAccountDto) => void;
}) {
  const titleId = useId();

  return (
    <Stack asChild gap={2}>
      <section aria-labelledby={titleId}>
        <Text size="xs" tone="muted" className="flex items-baseline gap-2">
          <Text inline id={titleId} weight="medium" className="uppercase tracking-wide">
            {title}
          </Text>
          {hint}
        </Text>
        <Stack asChild gap={2}>
          <ul>
            {accounts.map(account => (
              <AccountCard
                key={account.id}
                account={account}
                canManageSharedAccounts={canManageSharedAccounts}
                onDelete={onDelete}
              />
            ))}
          </ul>
        </Stack>
      </section>
    </Stack>
  );
}

export function AccountList({ onConnect }: { onConnect: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, status } = useQuery(emailAccountControllerListMineQueryOptions());

  const [accountToDelete, setAccountToDelete] = useState<EmailAccountDto | null>(null);
  const { mutateAsync: deleteAccount, isPending: isDeleting } =
    useEmailAccountControllerDisconnect();

  const confirmDelete = async () => {
    if (!accountToDelete) {
      return;
    }
    try {
      await deleteAccount({ emailAccountId: accountToDelete.id });
      await Promise.all(
        [
          emailAccountControllerListMineQueryKey(),
          generalInboxControllerGetSummaryQueryKey(),
          emailThreadControllerListThreadsQueryKey(),
        ].map(queryKey => queryClient.invalidateQueries({ queryKey })),
      );
      setAccountToDelete(null);
    } catch {
      toast({
        variant: 'destructive',
        title: t('connectedEmailAccounts.delete.errorTitle'),
        description: t('connectedEmailAccounts.delete.errorDescription'),
      });
    }
  };

  const accounts = data?.accounts ?? [];
  const groups = (['mine', 'shared'] as const)
    .map(group => ({
      group,
      accounts: accounts.filter(({ isShared }) => isShared === (group === 'shared')),
    }))
    .filter(({ accounts: groupAccounts }) => groupAccounts.length > 0);

  return (
    <DrawerPanel title={t('connectedEmailAccounts.title')}>
      <Stack gap={6}>
        <Button
          size="sm"
          leftIcon={<Plus className="size-4" />}
          onClick={onConnect}
          className="self-start"
        >
          {t('connectedEmailAccounts.connect')}
        </Button>
        {status === 'pending' && (
          <Stack gap={2}>
            {Array.from({ length: 3 }, (_, index) => (
              <Skeleton key={index} className="h-16 w-full rounded-lg" />
            ))}
          </Stack>
        )}
        {status === 'error' && (
          <Text size="sm" tone="muted">
            {t('connectedEmailAccounts.error')}
          </Text>
        )}
        {status === 'success' && groups.length === 0 && (
          <Text
            size="sm"
            tone="muted"
            className="rounded-lg border border-dashed px-4 py-6 text-center"
          >
            {t('connectedEmailAccounts.empty')}
          </Text>
        )}
        {groups.map(({ group, accounts: groupAccounts }) => (
          <AccountGroup
            key={group}
            title={t(`connectedEmailAccounts.groups.${group}.title`)}
            hint={t(`connectedEmailAccounts.groups.${group}.hint`)}
            accounts={groupAccounts}
            canManageSharedAccounts={data?.canManageSharedAccounts ?? false}
            onDelete={setAccountToDelete}
          />
        ))}
      </Stack>
      <DisconnectAccountDialog
        account={accountToDelete}
        onOpenChange={open => {
          if (!open && !isDeleting) {
            setAccountToDelete(null);
          }
        }}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
      />
    </DrawerPanel>
  );
}
